import React, {forwardRef, useContext, useMemo} from 'react';

import {FloatingMenuContext, FloatingPopover} from '@pb/kit-component-floating';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './menu.module.styl';

import type {FloatingMenuContextType, FloatingPopoverProps} from '@pb/kit-component-floating';


const classNames = withCssModule(styles);

export interface FloatingMenuProps extends FloatingPopoverProps {
    getItemProps: FloatingMenuContextType['getItemProps'],
    activeIndex: FloatingMenuContextType['activeIndex'],
    setActiveIndex?: (activeIndex: (number | null)) => void,
}

/**
 * Renders a floating menu.
 * Should be used with `useFloating` hook.
 * Supposed to be used inside other UI Kit components.
 * */
export const FloatingMenu = forwardRef<HTMLDivElement, FloatingMenuProps>((props, ref) => {
    const floatingMenuContextParentValue = useContext(FloatingMenuContext);

    const {
        children,
        className,
        getItemProps,
        activeIndex,
        portalProps = floatingMenuContextParentValue.portalProps,
        floatingFocusManagerProps = floatingMenuContextParentValue.floatingFocusManagerProps,
        ...floatingPopoverProps
    } = props;
    const floatingClasses = classNames(className, 'floating');


    const level = floatingMenuContextParentValue.level + 1;

    const floatingMenuContextValue = useMemo(() => ({
        getItemProps,
        activeIndex,
        level,
        portalProps,
        floatingFocusManagerProps,
    }), [getItemProps, activeIndex, level, portalProps, floatingFocusManagerProps]);

    return (
        <FloatingMenuContext.Provider value={floatingMenuContextValue}>
            <FloatingPopover
                ref={ref}
                aria-level={floatingMenuContextValue.level}
                floatingFocusManagerProps={floatingFocusManagerProps}
                portalProps={portalProps}
                {...floatingPopoverProps}
                className={floatingClasses}>
                {children}
            </FloatingPopover>
        </FloatingMenuContext.Provider>
    );
});

FloatingMenu.displayName = 'FloatingMenu';
